/*!
 * Copyright © 2005-2025 Hyland Software, Inc. and its affiliates. All rights reserved.
 *
 * Alfresco Example Content Application
 *
 * This file is part of the Alfresco Example Content Application.
 * If the software was purchased under a paid Alfresco license, the terms of
 * the paid license agreement will prevail. Otherwise, the software is
 * provided under the following open source license terms:
 *
 * The Alfresco Example Content Application is free software: you can redistribute it and/or modify
 * it under the terms of the GNU Lesser General Public License as published by
 * the Free Software Foundation, either version 3 of the License, or
 * (at your option) any later version.
 *
 * The Alfresco Example Content Application is distributed in the hope that it will be useful,
 * but WITHOUT ANY WARRANTY; without even the implied warranty of
 * MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE. See the
 * GNU Lesser General Public License for more details.
 *
 * You should have received a copy of the GNU Lesser General Public License
 * from Hyland Software. If not, see <http://www.gnu.org/licenses/>.
 */

import { HttpClient, HttpHeaders } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { AppConfigService, AuthenticationService } from '@alfresco/adf-core';
import { AlfrescoApiService } from '@alfresco/adf-content-services';
import { AppSettingsService } from '@alfresco/aca-shared';
import { forkJoin, from, Observable, of } from 'rxjs';
import { catchError, map, switchMap } from 'rxjs/operators';

/** Where a link stands against its expiry date. */
export type ShareLinkState = 'none' | 'active' | 'soon' | 'expired';

export interface ShareLinkRow {
  /** The share id — the last part of the public URL. */
  id: string;
  nodeId: string;
  name: string;
  sharedBy: string;
  sharedById: string;
  expiresAt: string | null;
  state: ShareLinkState;
  /** Visits counted by the jobs sidecar; undefined when it could not be asked. */
  opens?: number;
  lastOpenedAt?: string;
}

export interface ShareOpen {
  count: number;
  firstAt: string;
  lastAt: string;
}

/** A link this close to expiring is flagged, so it can be renewed or let go on purpose. */
export const EXPIRING_DAYS = 7;
/** Enough for any real repository; the screen says when the list was cut. */
const MAX_LINKS = 1000;
const PAGE = 100;
/** The sidecar answers for at most this many ids per request. */
const OPENS_PER_CALL = 200;

/**
 * Share links (the "send outside" links) and how often each was opened.
 *
 * The links themselves are Alfresco's: listing, expiry and revoking go through
 * its shared-links API, which shows a person the links on documents they can
 * read. Alfresco keeps no record of a link being used, so the open counts come
 * from the jobs sidecar, which the public link page reports to.
 */
@Injectable({ providedIn: 'root' })
export class ShareLinksService {
  private readonly api = inject(AlfrescoApiService);
  private readonly http = inject(HttpClient);
  private readonly auth = inject(AuthenticationService);
  private readonly appConfig = inject(AppConfigService);
  private readonly settings = inject(AppSettingsService);

  private call(path: string, method: string, query?: any): Observable<any> {
    const inst: any = this.api.getInstance();
    return from(inst.contentClient.callApi(path, method, {}, query ?? {}, {}, {}, null, ['application/json'], ['application/json']) as Promise<any>);
  }

  /**
   * Every link the caller can see, or only the ones they made; `truncated` when
   * there were more than the screen holds.
   *
   * "Mine" is filtered here, not with the API's `where=(sharedByUser='-me-')`:
   * on a repository searched by Elasticsearch that filter answers nothing at all,
   * whoever made the links.
   */
  list(mine: boolean, now = new Date()): Observable<{ rows: ShareLinkRow[]; truncated: boolean }> {
    const me = (this.auth.getUsername() ?? '').toLowerCase();
    const query = (skipCount: number) => ({ skipCount, maxItems: PAGE, include: 'properties' });
    const collect = (skipCount: number, acc: ShareLinkRow[]): Observable<{ rows: ShareLinkRow[]; truncated: boolean }> =>
      this.call('/shared-links', 'GET', query(skipCount)).pipe(
        switchMap((res) => {
          const batch: ShareLinkRow[] = (res?.list?.entries ?? [])
            .map((e: any) => ShareLinksService.toRow(e.entry, now))
            .filter((row: ShareLinkRow) => !mine || row.sharedById.toLowerCase() === me);
          const fetched = res?.list?.entries?.length ?? 0;
          const all = acc.concat(batch);
          const more = !!res?.list?.pagination?.hasMoreItems && fetched > 0;
          if (more && skipCount + fetched < MAX_LINKS) {
            return collect(skipCount + fetched, all);
          }
          return of({ rows: all, truncated: more });
        })
      );
    return collect(0, []);
  }

  /**
   * How often each link was opened. Never fails: without the sidecar the
   * screen still lists and revokes links, it just has no counts to show.
   */
  opens(ids: string[]): Observable<Record<string, ShareOpen> | null> {
    if (!ids.length) {
      return of({});
    }
    const base = `${this.appConfig.get<string>('jobs.baseUrl', '/jobs-api')}/api/share-links/opens`;
    const chunks: string[][] = [];
    for (let i = 0; i < ids.length; i += OPENS_PER_CALL) {
      chunks.push(ids.slice(i, i + OPENS_PER_CALL));
    }
    return forkJoin(
      chunks.map((chunk) => this.http.get<Record<string, ShareOpen>>(base, { params: { ids: chunk.join(',') }, headers: this.credentials() }))
    ).pipe(
      map((parts) => Object.assign({}, ...parts)),
      catchError(() => of(null))
    );
  }

  /** Ends a link: the public URL stops working at once. The document is not touched. */
  revoke(id: string): Observable<unknown> {
    return this.call(`/shared-links/${encodeURIComponent(id)}`, 'DELETE');
  }

  /** The public URL, built the way the share dialog builds it. */
  urlOf(id: string): string {
    return this.settings.baseShareUrl + id;
  }

  /** Where a link stands on `now`: no expiry, fine, expiring within a week, or already past. */
  static stateOf(expiresAt: string | null, now: Date): ShareLinkState {
    if (!expiresAt) {
      return 'none';
    }
    const left = new Date(expiresAt).getTime() - now.getTime();
    if (left <= 0) {
      return 'expired';
    }
    return left <= EXPIRING_DAYS * 24 * 60 * 60 * 1000 ? 'soon' : 'active';
  }

  private static toRow(entry: any, now: Date): ShareLinkRow {
    const expiresAt: string | null = entry?.expiresAt ?? entry?.properties?.['qshare:expiryDate'] ?? null;
    return {
      id: entry.id,
      nodeId: entry.nodeId,
      name: entry.name ?? '',
      sharedBy: entry.sharedByUser?.displayName ?? entry.sharedByUser?.id ?? '',
      sharedById: entry.sharedByUser?.id ?? '',
      expiresAt: expiresAt ? String(expiresAt) : null,
      state: ShareLinksService.stateOf(expiresAt ? String(expiresAt) : null, now)
    };
  }

  /** The sidecar asks Alfresco about the caller; a ticket travels in the ROLE_TICKET form. */
  private credentials(): HttpHeaders {
    const ticket = this.auth.getToken();
    return ticket ? new HttpHeaders({ Authorization: 'Basic ' + btoa(`ROLE_TICKET:${ticket}`) }) : new HttpHeaders();
  }
}
